import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import isString from 'lodash/isString';
import Vue from 'vue';

import {
    fetchSettings,
    fetchSetting,
    updateSetting,
    deleteSetting,
    parseValue,
} from '@/vuex/modules/user-settings/user-settings.utils';

import { HISTORY_TYPES, RECENTLY_SELECTED_HISTORY_TYPES } from './history/history.constants';

let loadAllPromise;

export default {
    namespaced: true,
    state: {
        error: null,
        fetching: false,
        map: {},
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { key, setting }) {
            Vue.set(state.map, key, setting);
        },
        deleteMapAtKey (state, { key }) {
            Vue.delete(state.map, key);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
    },
    actions: {
        async loadAll ({ state, commit, dispatch, rootGetters, rootState }, { reFetch = false } = {}) {
            if (!isEmpty(state.map) && !reFetch) {
                return;
            }

            if (reFetch) {
                loadAllPromise = undefined;
            }

            commit('setFetching', { fetching: true });

            try {
                // using ??= operator to prevent multiple requests if loadAll is called multiple times before the promise resolves
                loadAllPromise ??= fetchSettings();
                const settings = await loadAllPromise;
                let uniqueSettings = settings.reduce((acc, setting) => {
                    const isDuplicate = acc.find(({ name }) => name === setting.name);

                    if (!isDuplicate) {
                        const parsedValue = parseValue(setting.value);

                        acc.push({
                            name: setting.name,
                            value: parsedValue,
                        });
                    }

                    return acc;
                }, []);
                const appsWithSuggestedPages = rootGetters['apps/appsWithSuggestedPages'];
                const canCreatePages = rootGetters['auth/canCreatePages'];
                const isAdmin = rootGetters['auth/isAdmin'];
                const hasSuggestedPagesSetting = (canCreatePages || isAdmin) && appsWithSuggestedPages.length > 0;
                const { active: subscription } = rootState.subscription;
                const userPreferencesName = `${subscription.id}::userPreferences`;
                const userPreferences = {
                    name: userPreferencesName,
                    value: {
                        ...(hasSuggestedPagesSetting && {
                            suggestedPagesTable: {
                                value: true,
                                disabled: false,
                                subSettings: {
                                    notifications: {
                                        value: true,
                                        disabled: false,
                                    },
                                },
                            },
                        }),
                        ...get(
                            uniqueSettings.filter((setting) => setting.name === userPreferencesName),
                            '[0].value',
                            null,
                        ),
                    },
                };
                uniqueSettings = uniqueSettings.filter((setting) => setting.name !== userPreferencesName);
                if (!hasSuggestedPagesSetting) {
                    delete userPreferences.value.suggestedPagesTable;
                }
                if (
                    Object.keys(userPreferences.value).includes('newTaggingCenter') ||
                    Object.keys(userPreferences.value).includes('tagInASeparateBrowserWindow')
                ) {
                    // temporary cleanup for removing tagging in a separate browser window setting
                    delete userPreferences.value.newTaggingCenter;
                    delete userPreferences.value.tagInASeparateBrowserWindow;
                    await dispatch('update', {
                        name: 'userPreferences',
                        value: userPreferences.value,
                        namespaced: true,
                        crossUI: true,
                    });
                }
                uniqueSettings.push(userPreferences);

                const allHistoryTypes = [
                    ...Object.values(HISTORY_TYPES),
                    ...Object.values(RECENTLY_SELECTED_HISTORY_TYPES),
                ];

                const historySettings = uniqueSettings.reduce((acc, setting) => {
                    const [settingName, subId] = setting.name.split('--');

                    if (allHistoryTypes.includes(settingName) && subId === rootState.subscription.active.id) {
                        acc[settingName] = setting.value;
                    }

                    return acc;
                }, {});
                const settingsExcludingHistory = uniqueSettings.filter((setting) => {
                    const [settingName] = setting.name.split('--');

                    return !allHistoryTypes.includes(settingName);
                });

                commit('setMap', { map: keyBy(settingsExcludingHistory, 'name') });
                dispatch('userSettings/history/setHistory', { history: historySettings }, { root: true });
            } catch (error) {
                loadAllPromise = undefined;
                commit('setError', { error });
            }

            commit('setFetching', { fetching: false });
        },
        async loadOne (
            { commit, state, rootState },
            { reFetch = false, namespaced = false, name, crossUI = false } = {},
        ) {
            if (!reFetch) await loadAllPromise;

            let settingName = name;
            // PerSubscriptionUserStorage is namespaced
            if (namespaced) {
                const { active: subscription } = rootState.subscription;
                settingName = crossUI ? `${subscription.id}::${name}` : `${name}--${subscription.id}`;
            }

            if (state.map[settingName] && !reFetch) {
                return;
            }
            commit('setFetching', { fetching: true });
            try {
                const setting = await fetchSetting({ name: settingName });
                const parsedValue = parseValue(setting.value);

                commit('setFetching', { fetching: false });
                commit('setMapAtKey', {
                    key: setting.name,
                    setting: {
                        name: setting.name,
                        value: parsedValue,
                    },
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async update (
            { commit, rootState, state },
            { namespaced = false, name, value, force = false, crossUI = false },
        ) {
            let settingName = name;
            if (namespaced) {
                const { active: subscription } = rootState.subscription;
                settingName = crossUI ? `${subscription.id}::${name}` : `${name}--${subscription.id}`;
            }

            // prevent update if values are the same
            if (
                !force &&
                state.map[settingName] &&
                JSON.stringify(state.map[settingName].value) === JSON.stringify(value)
            ) {
                return;
            }

            let newValue = value;
            if (!isString(value)) {
                newValue = JSON.stringify(value);
            }

            try {
                const updatedSetting = await updateSetting({
                    name: settingName,
                    value: newValue,
                    userId: '{{uid}}',
                });

                commit('setMapAtKey', {
                    key: updatedSetting.name,
                    setting: {
                        name: updatedSetting.name,
                        value: parseValue(updatedSetting.value),
                    },
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async remove ({ commit, rootState }, { namespaced = false, name, crossUI = false }) {
            let settingName = name;
            if (namespaced) {
                const { active: subscription } = rootState.subscription;
                settingName = crossUI ? `${subscription.id}::${name}` : `${name}--${subscription.id}`;
            }

            commit('setFetching', { fetching: true });
            try {
                await deleteSetting({ name: settingName });
                commit('deleteMapAtKey', { key: settingName });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        settingByName: (state, getters, rootState) => (name, namespaced) => {
            if (namespaced) {
                name += `--${rootState.subscription.active.id}`;
            }

            return state.map[name];
        },
        crossUISettingByName: (state, getters, rootState) => (name) => {
            const { active: subscription } = rootState.subscription;
            const settingName = `${subscription.id}::${name}`;

            return state.map[settingName];
        },
    },
};
